/* eslint-disable react-hooks/exhaustive-deps */

import React, { useRef, useEffect } from "react";
import {
  I2cInput,
  I2cTooltip,
  I2cSelect,
  I2cMenuItem,
  I2cRadioGroup,
  I2cRadioButton,
  I2cCheckboxGroup,
  I2cRadio,
  I2cListItem,
  I2cDatePicker,
  I2cIcon,
  I2cCheckbox,
} from "@webcomponents/i2cwebcomponents/dist/react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import "../Prepaid.scss";
import { validationSchema } from "./validationSchema";
import { cardTechnology, atm_networks, subBin, cardInfo, cardNumberOptions, cardOptions, binType, binInformation, masterCardNetwork, visaCardNetwork, otherCardNetwork, region } from "./stringUtils";

const CardNetwork = ({
  type,
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  const additionalOpt = useRef(null);
  const checkboxRef = useRef(null);

  const handleNetworkCheckboxGroupChange = (event) => {
    let SelectedNetworkValue;
    if (isNaN(event)) SelectedNetworkValue = event.target.value;
    else SelectedNetworkValue = event;
    let updatedSelectedNetworks = [
      ...(methods.getValues("cn_atm_networks") ?? []),
    ];
    if (updatedSelectedNetworks.includes(SelectedNetworkValue)) {
      // If the card is already selected, remove it
      updatedSelectedNetworks = updatedSelectedNetworks.filter(
        (card) => card !== SelectedNetworkValue,
      );
    } else {
      // If the card is not selected, add it
      updatedSelectedNetworks.push(SelectedNetworkValue);
    }
    if (
      updatedSelectedNetworks.includes(5) &&
      methods.watch("cn_atm_networks_other").length
    ) {
      methods.setValue("cn_atm_networks_other", "");
    }
    methods.setValue("cn_atm_networks", updatedSelectedNetworks);
    methods.trigger();
    handleValueChanges();
  };

  const handleCardInfoChange = (event) => {
    let selectedCardInfo;
    if (isNaN(event)) selectedCardInfo = event.target.value;
    else selectedCardInfo = event;
    let updatedSelectedCardsInfo = [
      ...methods.getValues("cn_card_information"),
    ];

    if (updatedSelectedCardsInfo.includes(selectedCardInfo)) {
      // If the card is already selected, remove it
      updatedSelectedCardsInfo = updatedSelectedCardsInfo.filter(
        (card) => card !== selectedCardInfo,
      );
    } else {
      // If the card is not selected, add it
      updatedSelectedCardsInfo.push(selectedCardInfo);
    }
    if (!updatedSelectedCardsInfo.includes(4)) {
      methods.setValue("cn_card_information_other", "");
    }
    methods.setValue("cn_card_information", updatedSelectedCardsInfo);
    methods.trigger();
    handleValueChanges();
  };

  const handleAdditionaInfo = (event) => {
    let selectedCardAdditionalInfo;
    if (isNaN(event)) selectedCardAdditionalInfo = event.target.value;
    else selectedCardAdditionalInfo = event;
    let updatedAdditionalInfo = [
      ...methods.getValues("cn_additional_card_information"),
    ];

    if (updatedAdditionalInfo.includes(selectedCardAdditionalInfo)) {
      // If the card is already selected, remove it
      updatedAdditionalInfo = updatedAdditionalInfo.filter(
        (card) => card !== selectedCardAdditionalInfo,
      );
    } else {
      // If the card is not selected, add it
      updatedAdditionalInfo.push(selectedCardAdditionalInfo);
    }
    if (!updatedAdditionalInfo.includes(4)) {
      methods.setValue("cn_additional_card_information_other", "");
    }
    methods.setValue("cn_additional_card_information", updatedAdditionalInfo);
    methods.trigger();
    handleValueChanges();
  };
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      cn_n_networks: [],
      cn_n_networks_other: "",
      cn_atm_networks: [],
      cn_atm_networks_other: "",
      cn_bin_Information: "",
      cn_business_id: "",
      cn_ica: "",
      cn_bin_no: "",
      cn_settlement_currency: "",
      cn_bin_type: "",
      cn_bin_quantity: "",
      cn_bin_quantity_other: "",
      cn_target_date: "",
      cn_insurance_region: "",
      cn_additional_card_information: [],
      cn_additional_card_information_other: "",
      cn_chip_profile: "",
      cn_card_option: "",
      cn_card_number: "",
      cn_card_information: [],
      cn_card_information_other: "",
    },
  });
  const fields = [
    "cn_n_networks",
    "cn_n_networks_other",
    "cn_atm_networks",
    "cn_atm_networks_other",
    "cn_bin_Information",
    "cn_business_id",
    "cn_bin_no",
    "cn_settlement_currency",
    "cn_bin_type",
    "cn_bin_quantity",
    "cn_bin_quantity_other",
    "cn_target_date",
    "cn_insurance_region",
    "cn_additional_card_information",
    "cn_additional_card_information_other",
    "cn_chip_profile",
    "cn_card_option",
    "cn_card_number",
    "cn_card_information",
    "cn_card_information_other",
    "cn_ica",
  ];

  const [targetDateDefault, setTargetDateDefault] = React.useState("");

  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[2]["2_0"][field] ?? "");
      });
      const targetVal = formDataJson[2]["2_0"]["cn_target_date"] ?? "";
      if (targetVal) setTargetDateDefault(new Date(targetVal));
      methods.trigger();
      handleValueChanges();
    }
  }, [isFetching]);
  // Controlls destructuring from the method
  const {
    control,
    formState: { errors },
  } = methods;

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);

  const handleValueChanges = () => {
    const values = methods.getValues();
    setAddFormData((prev) => ({
      ...prev,
      2: {
        ...prev["2"],
        "2_0": { ...values },
      },
    }));
  };

  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) setFormValidStates("2_0", true);
    else setFormValidStates("2_0", false);
  }, [Object.keys(errors)?.length]);

  // Helper function to check if a network should be disabled
  const isNetworkDisabled = (value) => {
    const selectedNetworks = methods.watch("cn_n_networks") ?? [];

    // Mastercard (value: 0) should be disabled if Cirrus (1) or Maestro (2) is selected
    if (value === 0) {
      return selectedNetworks.includes(1) || selectedNetworks.includes(2);
    }

    // VISA (value: 3) should be disabled if Interlink (4) or PLUS (5) is selected
    if (value === 3) {
      return selectedNetworks.includes(4) || selectedNetworks.includes(5);
    }

    return false;
  };

  const handleCheckboxGroupChange = (value) => {
    let updatedSelectedNetworks = [
      ...(methods.getValues("cn_n_networks") ?? []),
    ];

    // Define parent-child relationships
    const mastercardChildren = [1, 2]; // Cirrus, Maestro
    const visaChildren = [4, 5]; // Interlink, PLUS

    if (updatedSelectedNetworks.includes(value)) {
      // If the card is already selected, remove it
      updatedSelectedNetworks = updatedSelectedNetworks.filter(
        (card) => card !== value,
      );

      // If removing Cirrus (1) or Maestro (2), check if Mastercard (0) should be removed
      if (mastercardChildren.includes(value)) {
        const hasOtherMastercardChildren = mastercardChildren.some(
          child => child !== value && updatedSelectedNetworks.includes(child)
        );
        if (!hasOtherMastercardChildren) {
          updatedSelectedNetworks = updatedSelectedNetworks.filter(card => card !== 0);
        }
      }

      // If removing Interlink (4) or PLUS (5), check if VISA (3) should be removed
      if (visaChildren.includes(value)) {
        const hasOtherVisaChildren = visaChildren.some(
          child => child !== value && updatedSelectedNetworks.includes(child)
        );
        if (!hasOtherVisaChildren) {
          updatedSelectedNetworks = updatedSelectedNetworks.filter(card => card !== 3);
        }
      }
    } else {
      // If the card is not selected, add it
      updatedSelectedNetworks.push(value);

      // Auto-select and disable Mastercard (0) if Cirrus (1) or Maestro (2) is selected
      if (mastercardChildren.includes(value) && !updatedSelectedNetworks.includes(0)) {
        updatedSelectedNetworks.push(0);
      }

      // Auto-select and disable VISA (3) if Interlink (4) or PLUS (5) is selected
      if (visaChildren.includes(value) && !updatedSelectedNetworks.includes(3)) {
        updatedSelectedNetworks.push(3);
      }
    }

    if (!updatedSelectedNetworks.includes(10)) {
      methods.setValue("cn_n_networks_other", "");
    }
    methods.setValue("cn_n_networks", updatedSelectedNetworks);
    handleValueChanges();
    methods.trigger();
    if (updatedSelectedNetworks.length) methods.clearErrors("cn_n_networks");
    else
      methods.setError("cn_n_networks", {
        message: "At least one network must be selected",
      });
  };


  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md" className="section-space">
          <div i2c-grid="12">
            <div className="sub-heading">
              Card Networks
            </div>
          </div>
          <div i2c-grid="12">
            <div className="single-field-wrapper">
              <Controller
                name="cn_n_networks"
                control={control}
                render={({ field }) => (
                  <>
                    <I2cCheckboxGroup
                      {...field}
                      className="centeredCheckbox checkbox-margin"
                      ref={checkboxRef}
                    >
                      <div className="sm-sub-heading mb-16">
                        Mastercard<sup>®</sup>
                      </div>
                      <div i2c-grid="grid gap-sm">
                        {masterCardNetwork.map((card, index) => {
                          const isChecked = methods.watch("cn_n_networks").includes(card.value);
                          const isDisabled = isNetworkDisabled(card.value);
                          return (
                            <I2cListItem
                              key={index}
                              i2c-grid="4@md"
                              variant="receipt"
                              label={card.label}
                              sublabel=""
                              value=""
                              type="checkbox"
                              disabled={isDisabled}
                              checked={isChecked}
                              className={ `cardNetwork-item ${isDisabled ? "disable-events" : ""}` }
                              onI2cCheckboxClicked={() =>
                                !isDisabled && handleCheckboxGroupChange(card.value)
                              }

                            >
                              <I2cIcon
                                slot="prefix"
                                src={card.icon}
                                style={{ fontSize: "var(--i2c-spacing-11-5)" }}
                                label="icon"
                              ></I2cIcon>
                              <I2cCheckbox
                                slot="checkbox"
                                name="cn_n_networks"
                                filled
                                value={card.value}
                                label=""
                                ignoreTab

                                checked={isChecked}
                                className="customCheckbox"
                              ></I2cCheckbox>
                            </I2cListItem>
                          );
                        })}
                      </div>
                      <div className="sm-sub-heading mb-16 mt-16">
                        VISA<sup>®</sup>
                      </div>
                      <div i2c-grid="grid gap-sm ">
                        {visaCardNetwork.map((card, index) => {
                          const isChecked = methods.watch("cn_n_networks").includes(card.value);
                          const isDisabled = isNetworkDisabled(card.value);
                          return (
                            <I2cListItem
                              key={index}
                              i2c-grid="4@md"
                              variant="receipt"
                              label={card.label}
                              sublabel=""
                              value=""
                              type="checkbox"
                              disabled={isDisabled}
                              checked={isChecked}
                              className={ `cardNetwork-item ${isDisabled ? "disable-events" : ""}` }
                              onI2cCheckboxClicked={() =>
                                !isDisabled && handleCheckboxGroupChange(card.value)
                              }
                            >
                              <I2cIcon
                                slot="prefix"
                                src={card.icon}
                                style={{ fontSize: "var(--i2c-spacing-11-5)" }}
                                label="icon"
                              ></I2cIcon>
                              <I2cCheckbox
                                slot="checkbox"
                                name="cn_n_networks"
                                filled
                                value={card.value}
                                label=""
                                ignoreTab
                                checked={isChecked}
                                className="customCheckbox"
                              ></I2cCheckbox>
                            </I2cListItem>
                          );
                        })}
                      </div>
                      <div className="sm-sub-heading mb-16 subsection-space mt-16">
                        Others
                      </div>
                      <div i2c-grid="grid gap-sm">
                        {otherCardNetwork.map((card, index) => (
                          <I2cListItem
                            key={index}
                            i2c-grid="4@md"
                            variant="receipt"
                            label={card.label}
                            className="cardNetwork-item"
                            sublabel=""
                            value=""
                            type="checkbox"
                            checked={methods
                              .watch("cn_n_networks")
                              .includes(card.value)}
                            onI2cCheckboxClicked={() =>
                              handleCheckboxGroupChange(card.value)
                            }
                          >
                            <I2cIcon
                              slot="prefix"
                              src={card.icon}
                              style={{ fontSize: "var(--i2c-spacing-11-5)" }}
                              label="icon"
                            ></I2cIcon>
                            <I2cCheckbox
                              slot="checkbox"
                              name="cn_n_networks"
                              filled
                              value={card.value}
                              label=""
                              ignoreTab
                              checked={methods
                                .watch("cn_n_networks")
                                .includes(card.value)}
                              className="customCheckbox"
                            ></I2cCheckbox>
                          </I2cListItem>
                        ))}
                      </div>
                    </I2cCheckboxGroup>
                    <ErrorMessage
                      show={
                        errors.cn_n_networks &&
                          methods.formState.touchedFields.cn_n_networks
                          ? true
                          : false
                      }
                      message={errors.cn_n_networks?.message ?? "Invalid input"}
                    />
                  </>
                )}
              />
            </div>
            {methods.watch("cn_n_networks").includes(10) && (
              <div i2c-grid="grid" className="single-field-wrapper mt-16">
                <div i2c-grid="4@md">
                  <Controller
                    name="cn_n_networks_other"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        name={"cn_n_networks_other"}
                        className="single-input-field"
                        placeholder="Please Specify"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          handleValueChanges();
                          if (value.length === 0) {
                            methods.setError("cn_n_networks_other", {
                              message: "Network other is required.",
                            });
                          } else {
                          }
                        }}
                        // required={errors.cn_n_networks_other ? true : false}
                        size="large"
                        label="Please Specify"
                      />
                    )}
                  />
                  {/* <ErrorMessage
                    show={
                      errors.cn_n_networks_other &&
                      methods.formState.touchedFields.cn_n_networks_other
                        ? true
                        : false
                    }
                    message={
                      errors.cn_n_networks_other?.message ?? "Invalid input"
                    }
                  /> */}
                </div>
              </div>
            )}
          </div>
        </div>
        <div i2c-grid="grid gap-md" className="section-space">
          <div i2c-grid="12">
            <div className="sub-heading mb-4">
              ATM Networks
            </div>
            <div className="custom-label-light">
              Select the networks that you have an existing relationship with.
            </div>
          </div>
          <div i2c-grid="12">
            <div className="single-field-wrapper">
              <Controller
                name="cn_atm_networks"
                control={control}
                render={({ field }) => (
                  <>
                    <I2cCheckboxGroup
                      {...field}
                      className="centeredCheckbox checkbox-margin"
                    >
                      <div i2c-grid="grid gap-sm">
                        {atm_networks.map((card, index) => (
                          <I2cListItem
                            key={index}
                            i2c-grid="4@md"
                            variant="receipt"
                            label={card.label}
                            sublabel=""
                            className="cardNetwork-item"
                            value=""
                            type="checkbox"
                            checked={methods
                              .watch("cn_atm_networks")
                              .includes(card.value)}
                            onI2cCheckboxClicked={(e) => {
                              handleNetworkCheckboxGroupChange(card.value);
                            }}
                          >
                            <I2cIcon
                              slot="prefix"
                              src={card.icon}
                              style={{ fontSize: "var(--i2c-spacing-11-5)" }}
                              label="icon"
                            ></I2cIcon>
                            <I2cCheckbox
                              slot="checkbox"
                              name="atmNetwork"
                              filled
                              value={card.value}
                              label=""
                              ignoreTab
                              checked={methods
                                .watch("cn_atm_networks")
                                .includes(card.value)}

                              className="customCheckbox"
                            ></I2cCheckbox>
                          </I2cListItem>
                        ))}
                      </div>
                    </I2cCheckboxGroup>
                    <ErrorMessage
                      show={
                        errors.cn_atm_networks &&
                          methods.formState.touchedFields.cn_atm_networks
                          ? true
                          : false
                      }
                      message={
                        errors.cn_atm_networks?.message ?? "Invalid input"
                      }
                    />
                  </>
                )}
              />
            </div>
            {methods.watch("cn_atm_networks").includes(5) && (
              <>
                <div
                  i2c-grid="grid 4@md"
                  className="mt-16 single-field-wrapper"
                >
                  <Controller
                    name="cn_atm_networks_other"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        name={"cn_atm_networks_other"}
                        className="single-input-field"
                        placeholder="Please Specify"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          handleValueChanges();
                          if (value.length === 0) {
                            methods.setError("cn_atm_networks_other", {
                              message: "Network other is required.",
                            });
                          } else {
                          }
                        }}
                        // required={errors.cn_atm_networks_other ? true : false}
                        size="large"
                        label="Please Specify"
                      />
                    )}
                  />
                </div>
                {/* <ErrorMessage
                  show={
                    errors.cn_atm_networks_other &&
                    methods.formState.touchedFields.cn_atm_networks_other
                      ? true
                      : false
                  }
                  message={
                    errors.cn_atm_networks_other?.message ?? "Invalid input"
                  }
                /> */}
              </>
            )}
          </div>
        </div>
        <div i2c-grid="grid gap-md" className="section-space">
          <div i2c-grid="12">
            <div className="sub-heading">
              BIN Information
            </div>
            <div className="custom-label-light">
              Do you have any specific BIN information?
            </div>
          </div>
          <div i2c-grid="12">
            <I2cRadioGroup
              onI2cChange={(e) => {
                const val = Number(e.target.value);
                methods.clearErrors("cn_bin_Information");
                methods.setValue("cn_bin_Information", val);
                if (val === 1) {
                  methods.setValue("cn_business_id", "");
                } else {
                  methods.setValue("cn_ica", "");
                }
                handleValueChanges();
              }}
            >
              {binInformation.map((bin, index) => (
                <I2cRadioButton
                  key={index}
                  name="cn_bin_Information"
                  value={bin.value}
                  label={bin.label}
                  checked={methods.watch("cn_bin_Information") === bin.value}
                >
                  {bin.label}
                </I2cRadioButton>
              ))}
            </I2cRadioGroup>
          </div>
          <div i2c-grid="12">
            <div i2c-grid="grid gap-column-sm gap-row-md">
              <div i2c-grid="6@md">
                <div className="single-field-wrapper">
                  {methods.watch("cn_bin_Information") === 1 ? (
                    <Controller
                      key="cn_ica"
                      name="cn_ica"
                      control={control}
                      render={({ field }) => (
                        <I2cInput
                          {...field}
                          name={"cn_ica"}
                          className={"single-input-field"}
                          placeholder="Enter ICA"
                          onI2cInput={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            handleValueChanges();
                          }}
                          invalid={errors.cn_ica ? true : false}
                          size="large"
                          label="ICA"
                        >
                          <I2cTooltip
                            slot="suffix"
                            hoist
                            content="Issued by the network. ICA (MasterCard only)."
                            placement="right-start"
                          >
                            <I2cIcon name="cs-linear-help-circle" color="#A1A3AE"></I2cIcon>
                          </I2cTooltip>
                        </I2cInput>
                      )}
                    />
                  ) : (
                    <Controller
                      key="cn_business_id"
                      name="cn_business_id"
                      control={control}
                      render={({ field }) => (
                        <I2cInput
                          {...field}
                          name={"cn_business_id"}
                          className={"single-input-field"}
                          placeholder="Enter Business ID"
                          onI2cInput={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            handleValueChanges();
                          }}
                          invalid={errors.cn_business_id ? true : false}
                          size="large"
                          label="Business ID"
                        >
                          <I2cTooltip
                            slot="suffix"
                            hoist
                            content="Issued by the network. BID (Visa only)."
                            placement="right-start"
                          >
                            <I2cIcon name="cs-linear-help-circle" color="#A1A3AE"></I2cIcon>
                          </I2cTooltip>
                        </I2cInput>
                      )}
                    />
                  )}
                </div>
              </div>
              <div i2c-grid="6@md">
                <div className="single-field-wrapper">
                  <Controller
                    name="cn_bin_no"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        name={"cn_bin_no"}
                        className={"single-input-field"}
                        placeholder="Enter BIN(s)"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          handleValueChanges();
                          if (value.length === 0) {
                            methods.setError("cn_bin_no", {
                              message: "Network other is required.",
                            });
                          } else {
                          }
                        }}
                        invalid={errors.cn_bin_no ? true : false}
                        size="large"
                        label="BIN(s)"

                      >
                        <I2cTooltip
                          slot="suffix"
                          hoist
                          content="The 6-8 Digit Bank Identification Number assigned by the network to
										support card issuance."
                          placement="right-start"
                        >
                          <I2cIcon name="cs-linear-help-circle" color="#A1A3AE"></I2cIcon>
                        </I2cTooltip>
                      </I2cInput>
                    )}
                  />
                  {/* <ErrorMessage
                    show={
                      errors.cn_bin_no &&
                      methods.formState.touchedFields.cn_bin_no
                        ? true
                        : false
                    }
                    message={errors.cn_bin_no?.message ?? "Invalid input"}
                  /> */}
                </div>
              </div>
              <div i2c-grid="6@md">

                <div className="single-field-wrapper">
                  <Controller
                    name="cn_settlement_currency"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        name={"cn_settlement_currency"}
                        className={"single-input-field"}
                        placeholder="Enter Settlement Currency"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          handleValueChanges();
                        }}
                        invalid={errors.cn_settlement_currency ? true : false}
                        size="large"
                        label="Settlement Currency"
                      >

                      </I2cInput>
                    )}
                  />
                  {/* <I2cSelect
                    label="Settlement Currency"
                    onI2cChange={(e) => {
                      const existingVal = methods.getValues(
                        "cn_settlement_currency",
                      );
                      if (e?.target?.value.length !== existingVal?.length) {
                        methods.setValue(
                          "cn_settlement_currency",
                          e.target.value,
                        );
                      }
                      if (e.target.value)
                        methods.clearErrors("cn_settlement_currency");
                      handleValueChanges();
                    }}
                    className={"single-input-field"}
                    value={methods.watch("cn_settlement_currency")}
                    placeholder="Select Currencies"
                    size="large"
                    multiple
                    helpText=""

                  >
                    {currencies.map((currency, index) => (
                      <I2cMenuItem key={index} value={currency.value}>
                        <i2c-avatar
                          image={currency.icon}
                          part="avatars"
                          label={currency.value}
                        ></i2c-avatar>
                        {currency.value}
                      </I2cMenuItem>
                    ))}
                  </I2cSelect> */}
                  <ErrorMessage
                    show={
                      errors.cn_settlement_currency &&
                        methods.formState.touchedFields.cn_settlement_currency
                        ? true
                        : false
                    }
                    message={
                      errors.cn_settlement_currency?.message ?? "Invalid input"
                    }
                  />
                </div>
              </div>
              <div i2c-grid="6@md">
                <div slot="label" className="organize-tooltip mb-6">
                  <div className="custom-label">
                    Type of BIN
                  </div>
                  <I2cTooltip
                    content="Select ‘Existing BIN’ if you have already setup card programs for
							      this BIN"
                    placement="right-start"
                  >
                    <I2cIcon label="placement" name="cs-linear-info" color="#6B7080"></I2cIcon>
                  </I2cTooltip>
                </div>
                <I2cRadioGroup
                  onI2cChange={(e) => {
                    methods.clearErrors("cn_bin_type");
                    methods.setValue("cn_bin_type", e.target.value);
                    handleValueChanges();
                  }}
                >
                  {binType.map((bin, index) => (
                    <I2cRadioButton
                      key={index}
                      size="large"
                      name="bi_binType"
                      value={bin.value}
                      label={bin.label}
                      checked={methods.watch("cn_bin_type") === bin.value}
                    >
                      {bin.label}
                    </I2cRadioButton>
                  ))}
                </I2cRadioGroup>
              </div>
            </div>
          </div>

          <div i2c-grid="12">
            <div slot="label" className="organize-tooltip mb-8">
              <div className="custom-label">
                {" "}
                Number of Sub-BINs desired
              </div>
              {/* <I2cTooltip content="" placement="right-start">
                <I2cIcon label="placement" name="cs-linear-info"></I2cIcon>
              </I2cTooltip> */}
            </div>
            <I2cRadioGroup
              onI2cChange={(e) => {
                const value = e.target.value;
                if (value !== "other")
                  methods.setValue("cn_bin_quantity_other", "");
                methods.setValue("cn_bin_quantity", value);
                methods.trigger();
                handleValueChanges();
              }}
            >
              <div i2c-grid="grid gap-sm">
                {subBin.map((bin, index) => (
                  <I2cListItem
                    key={index}
                    i2c-grid="4@md"
                    variant="receipt"
                    label={bin.label}
                    sublabel=""
                    value=" "
                    type="checkbox"
                    checked={methods.watch("cn_bin_quantity") === bin.value}
                  >
                    <I2cRadio
                      slot="prefix"
                      filled
                      name="cn_bin_quantity"
                      value={bin.value}
                      checked={methods.watch("cn_bin_quantity") === bin.value}
                      className="customCheckbox"
                    >
                      {" "}
                    </I2cRadio>
                  </I2cListItem>
                ))}
              </div>
            </I2cRadioGroup>
            {methods.watch("cn_bin_quantity") === "other" && (
              <div i2c-grid="grid 4@md" className="mt-16">
                <div className="single-field-wrapper">
                  <Controller
                    name="cn_bin_quantity_other"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        type="number"
                        className={"single-input-field"}
                        placeholder="Other Details"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          handleValueChanges();
                        }}
                        size="large"
                        label="Enter Details"
                        name={"cn_bin_quantity_other"}
                      />
                    )}
                  />
                  {/* <ErrorMessage
                    show={
                      errors.cn_bin_quantity_other &&
                      methods.formState.touchedFields.cn_bin_quantity_other
                        ? true
                        : false
                    }
                    message={
                      errors.cn_bin_quantity_other?.message ?? "Invalid input"
                    }
                  /> */}
                </div>
              </div>
            )}
          </div>

          <div i2c-grid="12">
            <div i2c-grid="grid gap-sm">
              <div i2c-grid="6@md">
                <div className="single-field-wrapper">
                  <Controller
                    name="cn_target_date"
                    control={control}
                    render={({ field }) => (
                      <>
                        <I2cDatePicker
                          {...field}
                          name="cn_target_date"
                          hoist
                          presetType="Range Group"
                          rotateIcon
                          errorMessageInvisible={true}
                          suffix='<i2c-icon name="linear-calendar-2" slot="suffix" class="suffix-iCon4"></i2c-icon>'
                          onI2cDateApply={(e) => {
                            if (e.target?.appliedDate) {
                              const dateStr = e.target?.appliedDate[0]?.toDateString() ?? "";
                              field.onChange(dateStr);
                              setTargetDateDefault(dateStr ? new Date(dateStr) : "");
                            } else {
                              field.onChange("");
                              setTargetDateDefault("");
                            }
                            handleValueChanges();
                          }}
                          invalid={errors.cn_target_date ? true : false}
                          I2cDatePicker
                          dateFormat="F d, Y"
                          label="Target Install/Migration Date"
                          size="large"
                          defaultDate={targetDateDefault}
                        />
                        <span slot="label"></span>
                      </>
                    )}
                  />
                  <ErrorMessage
                    show={
                      errors.cn_target_date &&
                        methods.formState.touchedFields.cn_target_date
                        ? true
                        : false
                    }
                    message={errors.cn_target_date?.message ?? "Invalid input"}
                  />
                </div>
              </div>
              <div i2c-grid="6@md">
                <div className="single-field-wrapper" i2c-grid="6@md">
                  <Controller
                    name="cn_insurance_region"
                    control={control}
                    render={({ field }) => (
                      <I2cSelect
                        {...field}
                        name="cn_insurance_region"
                        hoist 
                        search
                        onI2cChange={(e) => {
                          methods.setValue(
                            "cn_insurance_region",
                            e.target.value,
                          );
                          if (e.target.value)
                            methods.clearErrors("cn_insurance_region");
                          handleValueChanges();
                        }}
                        // required={errors.cn_insurance_region ? true : false}
                        className={"single-input-field"}
                        label="Issuance Country/Region"
                        value={methods.watch("cn_insurance_region")}
                        placeholder="Select Issuance Country/Region"
                        size="large"
                        helpText=""
                      >
                        {region.map((country, index) => (
                          <I2cMenuItem key={index} value={country}>
                            {country}
                          </I2cMenuItem>
                        ))}
                      </I2cSelect>
                    )}
                  />
                  <ErrorMessage
                    show={
                      errors.cn_insurance_region &&
                        methods.formState.touchedFields.cn_insurance_region
                        ? true
                        : false
                    }
                    message={
                      errors.cn_insurance_region?.message ?? "Invalid input"
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div i2c-grid="12">
            <span slot="label" className="organize-tooltip mb-16">
              {type !== "credit" ? (
                <div className="custom-label">Card Technology&nbsp;</div>
              ) : (
                <div className="custom-label"> Card Technology&nbsp;</div>
              )}

              <I2cTooltip
                content="Magstripe, EMV Chip & PIN – contact or contactless
							chip in addition to magnetic stripe for enhanced security. Contactless – conduct contactless payment
							transactions"
                placement="right-start"
                hoist
              >
                <I2cIcon label="placement" name="cs-linear-info" color="#6B7080"></I2cIcon>
              </I2cTooltip>
            </span>
            <I2cCheckboxGroup
              name="cn_additional_card_information"
              ref={additionalOpt}
            >
              <div i2c-grid="grid gap-sm">
                {cardTechnology.map((card, index) => (
                  <div key={index} i2c-grid="4@md ">
                    <I2cListItem
                      variant="receipt"
                      label={card.label}
                      sublabel=""
                      value=""
                      type="checkbox"
                      checked={methods
                        .watch("cn_additional_card_information")
                        .includes(card.value)}
                      onI2cCheckboxClicked={(e) =>
                        handleAdditionaInfo(card.value)
                      }
                    >
                      <I2cCheckbox
                        slot="prefix"
                        name="co_infoShare"
                        value={card.value}
                        filled
                        label=""
                        ignoreTab
                        checked={methods
                          .watch("cn_additional_card_information")
                          .includes(card.value)}
                        // onI2cChange={(e) => handleAdditionaInfo(e)}
                        className="customCheckbox"
                      ></I2cCheckbox>
                    </I2cListItem>
                  </div>
                ))}
              </div>
            </I2cCheckboxGroup>
            {methods.watch("cn_additional_card_information").includes(4) && (
              <div i2c-grid="grid 4@md" className={"mt-16"}>
                <div className="single-field-wrapper">
                  <Controller
                    name="cn_additional_card_information_other"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}
                        className={"single-input-field"}
                        // className={style.halfWidth + " single-input-field"}
                        placeholder="Please Specify"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          handleValueChanges();
                        }}
                        invalid={
                          errors.cn_additional_card_information_other
                            ? true
                            : false
                        }
                        size="large"
                        label="Enter Details"
                        name={"cn_additional_card_information_other"}
                      />
                    )}
                  />
                  {/* <ErrorMessage
                    show={
                      errors.cn_additional_card_information_other &&
                      methods.formState.touchedFields
                        .cn_additional_card_information_other
                        ? true
                        : false
                    }
                    message={
                      errors.cn_additional_card_information_other?.message ??
                      "Invalid input"
                    }
                  /> */}
                </div>
              </div>
            )}
          </div>
          <div i2c-grid="4@md">
            <div className="single-field-wrapper">
              <Controller
                name="cn_chip_profile"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className={"single-input-field"}
                    placeholder="Enter Chip Profile"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    invalid={errors.cn_chip_profile ? true : false}
                    size="large"
                    label="Chip Profile"
                    name={"cn_chip_profile"}

                  >
                    <I2cTooltip
                      slot="suffix"
                      hoist
                      content="Please confirm the chip profile recommended by the Network"
                      placement="right-start"
                    >
                      <I2cIcon name="cs-linear-help-circle" color="#A1A3AE"></I2cIcon>
                    </I2cTooltip>
                  </I2cInput>
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cn_chip_profile &&
                  methods.formState.touchedFields.cn_chip_profile
                    ? true
                    : false
                }
                message={errors.cn_chip_profile?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
        </div>
        <div i2c-grid="grid gap-md" className="section-space">
          <div i2c-grid="12">
            <div className="sub-heading mb-4">
              Card Options
            </div>
            <div className="custom-label-light mb-24">
              Please confirm the card options for this card program.
            </div>
            <I2cRadioGroup
              name="cn_card_option"
              onI2cChange={(e) => {
                const value = e.target.value ?? "";
                methods.setValue("cn_card_option", value);
                if (value === "virtual-cards" || value === "physical-cards") {
                  methods.setValue("cn_card_number", "");
                }
                methods.trigger();
                handleValueChanges();

              }}
            >
              <div i2c-grid="grid gap-sm">
                {cardOptions.map((option, index) => (
                  <I2cListItem
                    key={index}
                    i2c-grid="6@md"
                    variant="receipt"
                    label={option.label}
                    style={{ display: "flex", justifyContent: "space-between" }}
                    sublabel=""
                    className="option-container cardlist-custom-direction"
                    checked={
                      methods.getValues("cn_card_option") === option.value
                    }
                    type="checkbox"
                  >
                    <I2cRadio
                      slot="prefix"
                      filled
                      checked={
                        methods.getValues("cn_card_option") === option.value
                      }
                      name="cardOption"
                      value={option.value}
                    ></I2cRadio>
                    <I2cTooltip
                      slot="suffix"
                      hoist
                      content={option.supportText}
                      placement="right-start"
                    >
                      <I2cIcon
                        color="#A1A3AE"
                        name="cs-linear-help-circle"
                      ></I2cIcon>
                    </I2cTooltip>
                  </I2cListItem>
                ))}
              </div>
            </I2cRadioGroup>
          </div>
          {(methods.watch("cn_card_option") ===
            "virtual-and-physical-simultaneous" ||
            methods.watch("cn_card_option") ===
            "virtual-and-physical-on-demand") && (
              <div i2c-grid="12">
                <div className="sub-heading">
                  Card Number
                </div>
                <div className="custom-label-light mb-16">
                  If both Virtual and Physical cards are to be issued, will they
                  have:
                </div>
                <I2cRadioGroup
                  onI2cChange={(e) => {
                    const value = e.target.value ?? "";
                    methods.setValue("cn_card_number", value);
                    handleValueChanges();
                    methods.clearErrors("cn_card_number");
                  }}
                >
                  <div i2c-grid="grid">
                    {cardNumberOptions.map((option, index) => (
                      <I2cListItem
                        key={index}
                        i2c-grid="6@md"
                        variant="receipt"
                        label={option.value}
                        sublabel=""
                        className="option-container cardlist-custom-direction "
                        checked={
                          methods.getValues("cn_card_number") === option.value
                        }
                        type="checkbox"
                      >
                        <I2cRadio
                          slot="prefix"
                          filled
                          value={option.value}
                          checked={
                            methods.getValues("cn_card_number") === option.value
                          }
                        ></I2cRadio>
                        <I2cTooltip
                          slot="suffix"
                          hoist
                          content={option.label}
                          placement="right-start"
                        >
                          <I2cIcon
                            color="#A1A3AE"
                            name="cs-linear-help-circle"
                          ></I2cIcon>
                        </I2cTooltip>
                      </I2cListItem>
                    ))}
                  </div>
                </I2cRadioGroup>
              </div>
            )}
          <div i2c-grid="12 gap-sm">
            <div className="sub-heading mb-4">Card Information</div>
            <div className="custom-label-light mb-16">
              How will the virtual card information be shared with the
              cardholders?
            </div>
            <I2cCheckboxGroup name="cn_card_information" ref={checkboxRef}>
              <div i2c-grid="grid gap-sm">
                {cardInfo.map((card, index) => (
                  <div key={index} i2c-grid="6@md">
                    <I2cListItem
                      variant="receipt"
                      label={card.label}
                      sublabel=""
                      value=""
                      type="checkbox"
                      checked={methods
                        .watch("cn_card_information")
                        .includes(card.value)}
                      onI2cCheckboxClicked={(e) =>
                        handleCardInfoChange(card.value)
                      }
                    >
                      <I2cCheckbox
                        slot="prefix"
                        value={card.value}
                        label=""
                        ignoreTab
                        filled
                        checked={methods
                          .watch("cn_card_information")
                          .includes(card.value)}
                        // onI2cChange={(e) => handleCardInfoChange(e)}
                        className="customCheckbox"
                      ></I2cCheckbox>
                    </I2cListItem>
                  </div>
                ))}
              </div>
            </I2cCheckboxGroup>
            {methods.watch("cn_card_information").includes(4) && (
              <div i2c-grid="grid 6@md" className="mt-16">
                <div className="single-field-wrapper">
                  <Controller
                    name="cn_card_information_other"
                    control={control}
                    render={({ field }) => (
                      <I2cInput
                        {...field}

                        className={"single-input-field"}
                        placeholder="Other Details"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          field.onChange(value);
                          handleValueChanges();
                        }}
                        invalid={errors.cn_card_information_other ? true : false}
                        size="large"
                        label="Enter Details"
                        name={"cn_card_information_other"}
                      />
                    )}
                  />
                  {/* <ErrorMessage
                    show={
                      errors.cn_card_information_other &&
                      methods.formState.touchedFields.cn_card_information_other
                        ? true
                        : false
                    }
                    message={
                      errors.cn_card_information_other?.message ?? "Invalid input"
                    }
                  /> */}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CardNetwork;
